import React, { useState, useEffect } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import GroupSidebar from './GroupSidebar';
import ChatArea from './ChatArea';
import ThreadPanel from './ThreadPanel';
import DetailsPanel from './DetailsPanel';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';

interface TeamChatLayoutProps {
  isGlobalDarkMode: boolean;
}

const TeamChatLayout: React.FC<TeamChatLayoutProps> = ({ isGlobalDarkMode }) => {
  const { activeThreadId, activeChannelId, activeDmId } = useTeamChat();
  const [showDetails, setShowDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return document.body.getAttribute('data-sidebar-collapsed') === 'true' || window.innerWidth < 768;
  });
  useEffect(() => {
    const handleToggle = (e: any) => {
      if (typeof e.detail?.collapsed === 'boolean') {
        setIsSidebarCollapsed(e.detail.collapsed);
      } else {
        setIsSidebarCollapsed(prev => !prev);
      }
    };
    window.addEventListener('toggle-secondary-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-secondary-sidebar', handleToggle);
  }, []);

  const hasChatSelected = activeChannelId || activeDmId;

  if (isMobile) {
    // If a chat is selected, show full-screen chat (no bottom nav visible)
    if (hasChatSelected) {
      return (
        <div key="chat-view" className="fixed inset-0 z-[100] flex flex-col bg-background">
          {/* Show thread or details in full screen on mobile, otherwise show chat */}
          {activeThreadId ? (
            <ThreadPanel isGlobalDarkMode={isGlobalDarkMode} isMobile={isMobile} showDetails={showDetails} />
          ) : showDetails ? (
            <DetailsPanel onClose={() => setShowDetails(false)} isGlobalDarkMode={isGlobalDarkMode} isMobile={isMobile} />
          ) : (
            <ChatArea
              showDetails={showDetails}
              onToggleDetails={() => setShowDetails(!showDetails)}
              isGlobalDarkMode={isGlobalDarkMode}
              isMobile={isMobile}
              onOpenSidebar={() => {}}
            />
          )}
        </div>
      );
    }

    // No chat selected - show full-screen sidebar
    return (
      <div key="sidebar-view" className="h-full flex flex-col bg-background">
        <GroupSidebar
          isGlobalDarkMode={isGlobalDarkMode}
          hideHeader={false}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex">
      <div
        className={`fixed left-16 top-14 h-[calc(100vh-3.5rem)] w-56 border-r transition-all duration-300 z-20 flex flex-col ${
          isSidebarCollapsed ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
        } ${isGlobalDarkMode ? 'bg-sidebar border-sidebar-border' : 'bg-sidebar-light-secondary border-gray-200'}`}
      >
        <GroupSidebar isGlobalDarkMode={isGlobalDarkMode} />
      </div>
      <div className="flex-1 min-w-0 flex">
        <ResizablePanelGroup direction="horizontal" className="h-full flex-1 min-w-0">
          <ResizablePanel defaultSize={activeThreadId ? 65 : 100} minSize={40}>
            <ChatArea
              showDetails={showDetails}
              onToggleDetails={() => setShowDetails(!showDetails)}
              isGlobalDarkMode={isGlobalDarkMode}
              isMobile={false}
            />
          </ResizablePanel>
          {activeThreadId && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={35} minSize={25} maxSize={50}>
                <ThreadPanel isGlobalDarkMode={isGlobalDarkMode} showDetails={showDetails} />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
        
        {showDetails && (
          <div className="flex-shrink-0 h-full">
            <DetailsPanel onClose={() => setShowDetails(false)} isGlobalDarkMode={isGlobalDarkMode} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamChatLayout;
